<template>
  <section class="dp-layout sc-attendance-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick"></DepartmentPane>
    </section>

    <section class="dp-right sc-attendance-page">
      <toolbar ref="toolbar" :buttonList="toolbarButtons" @callFunction="callFunction" :searchList="searchList"></toolbar>

      <template v-if="listLoading || (list && list.length > 0)">
        <div class="tbl-scroll-host">
          <el-table
            ref="statTable"
            :data="pagedList"
            v-loading="listLoading"
            size="small"
            class="sc-stat-table"
            height="100%">
            <el-table-column label="课次 / 班级 / 范围" min-width="260" align="left" class-name="sc-td-session">
              <template slot-scope="scope">
                <div class="session-cell">
                  <div class="session-cell__meta">
                    <span class="session-cell__date">{{ scope.row.Date }}</span>
                    <span class="session-cell__time">
                      <i class="el-icon-time"></i>
                      {{ scope.row.TimeName }}{{ scope.row.TimeRange ? ' · ' + scope.row.TimeRange : '' }}
                    </span>
                  </div>
                  <div class="session-cell__title" :title="scope.row.SessionName">{{ scope.row.SessionName }}</div>
                  <div class="session-cell__sub">
                    <span v-if="scope.row.ClassName" class="session-cell__class">{{ scope.row.ClassName }}</span>
                    <el-tooltip
                      v-if="scope.row.StudentScopeSummary"
                      effect="dark"
                      placement="top"
                      :content="scope.row.StudentScopeSummary"
                    >
                      <span class="session-cell__scope">{{ scope.row.StudentScopeSummary }}</span>
                    </el-tooltip>
                  </div>
                  <div
                    v-if="scope.row.HasAdjustmentRecord || scope.row.hasAdjustmentRecord"
                    class="session-cell__adj"
                  >
                    <el-tag type="warning" size="mini" effect="plain">代调课</el-tag>
                    <el-tooltip
                      v-if="scope.row.AdjustmentPayrollNote || scope.row.adjustmentPayrollNote"
                      effect="dark"
                      placement="top"
                      :content="scope.row.AdjustmentPayrollNote || scope.row.adjustmentPayrollNote"
                    >
                      <span class="session-cell__adj-text">备注</span>
                    </el-tooltip>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="人数" min-width="220" align="center" class-name="sc-td-count">
              <template slot-scope="scope">
                <div class="stat-chips">
                  <span class="stat-chip stat-chip--total"><em>应到</em><strong>{{ scope.row.Total }}</strong></span>
                  <span class="stat-chip stat-chip--ok"><em>正常</em><strong>{{ scope.row.Normal }}</strong></span>
                  <span class="stat-chip" :class="{ 'stat-chip--late': (scope.row.LateOrEarlyLeave || 0) > 0 }"><em>迟/退</em><strong>{{ scope.row.LateOrEarlyLeave || 0 }}</strong></span>
                  <span class="stat-chip" :class="{ 'stat-chip--bad': scope.row.Miss > 0 }"><em>缺勤</em><strong>{{ scope.row.Miss }}</strong></span>
                  <span class="stat-chip" :class="{ 'stat-chip--warn': scope.row.Leave > 0 }"><em>请假</em><strong>{{ scope.row.Leave }}</strong></span>
                  <span class="stat-chip stat-chip--muted"><em>其他</em><strong>{{ scope.row.Other }}</strong></span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="出勤率" width="108" align="center" class-name="sc-td-rate">
              <template slot-scope="scope">
                <div class="rate-cell">
                  <span class="rate-cell__num" :style="rateNumStyle(scope.row)">{{ calculateRate(scope.row) }}%</span>
                  <el-progress
                    :percentage="calculateRate(scope.row)"
                    :color="rateProgressColor(scope.row)"
                    :stroke-width="5"
                    :show-text="false"
                    class="rate-cell__bar"
                  />
                </div>
              </template>
            </el-table-column>
            <el-table-column label="点名 / 值日" min-width="280" width="300" align="left" class-name="sc-td-duty">
              <template slot-scope="scope">
                <div class="duty-card" :class="dutyRollBlockClass(scope.row)">
                  <div class="duty-card__head">
                    <span v-if="scope.row.IsMarked" class="duty-badge duty-badge--ok">{{ markIsMakeup(scope.row) ? '已点名·补点' : '已点名' }}</span>
                    <span v-else-if="markSupplementary(scope.row)" class="duty-badge duty-badge--warn">补点</span>
                    <span v-else class="duty-badge duty-badge--muted">未点名</span>
                    <el-button
                      v-if="scope.row.IsMarked"
                      type="text"
                      size="mini"
                      class="duty-mark-count-btn"
                      @click="viewMarkHistory(scope.row)"
                    >{{ markCountField(scope.row) > 0 ? (markCountField(scope.row) + '次履历') : '履历' }}</el-button>
                    <span
                      v-if="scope.row.IsMarked && markedTimeField(scope.row)"
                      class="duty-card__time"
                    >{{ formatTime(markedTimeField(scope.row)) }}</span>
                  </div>
                  <div class="duty-card__body">
                    <!-- 点名 | 任教：左右两列 -->
                    <div class="duty-teachers">
                      <div v-if="markedTeacherField(scope.row)" class="duty-teachers__item">
                        <span class="duty-line__k">点名</span>
                        <span class="duty-line__v">{{ markedTeacherField(scope.row) }}</span>
                      </div>
                      <div class="duty-teachers__item">
                        <span class="duty-line__k">任教</span>
                        <span
                          class="duty-line__v"
                          :class="{ 'duty-line__v--empty': !dutyTeachingCellDisplay(scope.row) }"
                        >{{ dutyTeachingCellDisplay(scope.row) || '暂无任教' }}</span>
                      </div>
                    </div>
                    <div v-if="dutyField(scope.row, 'dutyCheckOperatorName')" class="duty-line duty-line--inline">
                      <span class="duty-line__k">提交</span>
                      <span class="duty-line__v">{{ dutyField(scope.row, 'dutyCheckOperatorName') }}</span>
                      <template v-if="dutySubmitTime(scope.row)">
                        <span class="duty-pair__dot">·</span>
                        <span class="duty-line__v duty-line__v--mono">{{ formatTime(dutySubmitTime(scope.row)) }}</span>
                      </template>
                    </div>
                    <div
                      v-else-if="dutySubmitTime(scope.row)"
                      class="duty-line duty-line--inline"
                    >
                      <span class="duty-line__k">提交</span>
                      <span class="duty-line__v duty-line__v--mono">{{ formatTime(dutySubmitTime(scope.row)) }}</span>
                    </div>
                    <div v-if="dutyLineScheduledVisible(scope.row)" class="duty-line duty-line--inline">
                      <span class="duty-line__k">原老师</span><span class="duty-line__v">{{ dutyField(scope.row, 'scheduledTeachersSummary') }}</span>
                    </div>
                    <div v-if="dutyField(scope.row, 'substituteTeachersSummary')" class="duty-line duty-line--inline">
                      <span class="duty-line__k">代班</span><span class="duty-line__v duty-line__v--accent">{{ dutyField(scope.row, 'substituteTeachersSummary') }}</span>
                    </div>
                    <div v-if="dutyAttendanceText(scope.row)" class="duty-line duty-line--inline">
                      <span class="duty-line__k">值日</span>
                      <span class="duty-line__v" :class="dutyAttendanceClass(scope.row)">{{ dutyAttendanceText(scope.row) }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100" align="center" fixed="right" class-name="sc-td-action">
              <template slot-scope="scope">
                <div class="sc-action-cell">
                  <el-button type="primary" plain size="mini" icon="el-icon-view" @click="viewDetail(scope.row)">明细</el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <el-col v-show="list.length > 0 && !listLoading" :span="24" class="toolbar sc-stat-pager">
          <el-pagination
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pageSize"
            :total="list.length"
            @current-change="onStatPageChange"
            @size-change="onStatPageSizeChange"
            style="float: right"
          />
        </el-col>
      </template>

      <div v-else-if="!listLoading && departmentId" class="sc-stat-empty">
        <i class="el-icon-document"></i>
        <p>所选日期暂无考勤记录</p>
        <p class="sc-stat-empty__hint">请调整日期、时段模板或切换左侧机构后重新查询</p>
      </div>

      <div v-else-if="!listLoading && !departmentId" class="sc-stat-empty">
        <i class="el-icon-s-home"></i>
        <p>请先在左侧选择学校或机构</p>
        <p class="sc-stat-empty__hint">选择后将自动加载统计数据</p>
      </div>

      <!-- 明细弹窗：标题栏右侧「导出明细」 -->
      <el-dialog class="detail-dialog" :visible.sync="detailVisible" width="50%" append-to-body
        :close-on-click-modal="false">
        <template slot="title">
          <div class="detail-dialog-title">
            <span class="detail-dialog-title-text">{{ detailTitle }}</span>
            <el-button type="primary" style="margin-left: 20px;" plain size="small" icon="el-icon-download" :loading="exportDetailLoading"
              @click.stop="exportDetail">导出明细</el-button>
          </div>
        </template>
        <div class="detail-toolbar">
          <el-input
            v-model="detailKeyword"
            clearable
            size="small"
            placeholder="搜索学生姓名"
            prefix-icon="el-icon-search"
            class="detail-search-input"
            @keyup.enter.native="onDetailSearch"
            @clear="onDetailSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="onDetailSearch"></el-button>
          </el-input>
          <el-select
            v-model="detailAttendanceState"
            clearable
            size="small"
            placeholder="考勤状态"
            class="detail-state-select"
            @change="onDetailSearch"
          >
            <el-option label="全部状态" :value="''" />
            <el-option label="正常" :value="1" />
            <el-option label="迟/退" :value="4" />
            <el-option label="缺勤" :value="0" />
            <el-option label="请假" :value="2" />
            <el-option label="其他" :value="3" />
            <el-option label="无记录" :value="-1" />
          </el-select>
        </div>
        <el-table :data="detailList" v-loading="detailLoading" border size="small" height="420">
          <el-table-column prop="StudentName" label="学生姓名" width=""></el-table-column>
          <el-table-column prop="ClassName" label="行政班级" width="" show-overflow-tooltip></el-table-column>
          <el-table-column prop="AttendanceState" label="状态" width="" align="center">
            <template slot-scope="scope">
              <el-tag :type="getDetailStateTag(scope.row)" size="mini">
                {{ getDetailStateText(scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="点名教师" width="" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <span v-if="scope.row.MarkedTeacher">{{ scope.row.MarkedTeacher }}</span>
              <span v-else style="color: #c0c4cc">-</span>
            </template>
          </el-table-column>
          <el-table-column label="点名时间" width="140" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.MarkedTime">{{ formatTime(scope.row.MarkedTime) }}</span>
              <span v-else style="color: #c0c4cc">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="Remark" label="备注" width="" show-overflow-tooltip></el-table-column>
        </el-table>
        <div slot="footer" class="detail-dialog-footer">
          <el-pagination class="detail-pager" layout="total, sizes, prev, pager, next, jumper"
            :current-page.sync="detailPage" :page-sizes="[10, 20, 50]" :page-size="detailPageSize" :total="detailTotal"
            @size-change="onDetailSizeChange" @current-change="onDetailPageChange" />
          <el-button @click="detailVisible = false" size="small">关闭</el-button>
        </div>
      </el-dialog>

      <!-- 班次点名履历 -->
      <el-dialog
        custom-class="mark-history-dialog"
        :title="markHistoryTitle"
        :visible.sync="markHistoryVisible"
        width="820px"
        top="6vh"
        append-to-body
        :close-on-click-modal="false"
      >
        <div class="mh-body" v-loading="markHistoryLoading">
          <el-table
            :data="markHistoryPagedList"
            border
            size="small"
            height="420"
            class="mh-table"
            empty-text="暂无点名履历"
          >
            <el-table-column prop="SeqNo" label="次数" width="72" align="center">
              <template slot-scope="scope">
                <span class="mh-seq">第 {{ scope.row.SeqNo }} 次</span>
              </template>
            </el-table-column>
            <el-table-column label="点名教师" min-width="100" align="center" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.OperatorTeacherName || '-' }}</template>
            </el-table-column>
            <el-table-column label="点名时间" width="158" align="center">
              <template slot-scope="scope">{{ formatTime(scope.row.SubmitTime) }}</template>
            </el-table-column>
            <el-table-column label="补点" width="72" align="center">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.IsMakeup" type="warning" size="mini" effect="plain">补点</el-tag>
                <span v-else class="mh-dash">—</span>
              </template>
            </el-table-column>
            <el-table-column label="摘要" min-width="240" align="left">
              <template slot-scope="scope">
                <div class="mh-summary">
                  <span class="mh-chip mh-chip--total">共 {{ scope.row.StudentCount || 0 }}</span>
                  <span class="mh-chip mh-chip--ok">正常 {{ scope.row.NormalCount || 0 }}</span>
                  <span class="mh-chip mh-chip--late">迟/退 {{ scope.row.LateOrEarlyLeaveCount || 0 }}</span>
                  <span class="mh-chip mh-chip--warn">请假 {{ scope.row.LeaveCount || 0 }}</span>
                  <span class="mh-chip mh-chip--bad">缺勤 {{ scope.row.MissCount || 0 }}</span>
                  <span class="mh-chip mh-chip--muted">其他 {{ scope.row.OtherCount || 0 }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="88" align="center" fixed="right">
              <template slot-scope="scope">
                <el-button type="primary" plain size="mini" @click="viewMarkBatchDiff(scope.row)">对比</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div slot="footer" class="detail-dialog-footer">
          <el-pagination
            class="detail-pager"
            layout="total, sizes, prev, pager, next, jumper"
            :current-page.sync="markHistoryPage"
            :page-sizes="[10, 20, 50]"
            :page-size="markHistoryPageSize"
            :total="markHistoryList.length"
            @size-change="onMarkHistorySizeChange"
            @current-change="onMarkHistoryPageChange"
          />
          <el-button size="small" @click="markHistoryVisible = false">关闭</el-button>
        </div>
      </el-dialog>

      <!-- 某次点名 vs 上次：学生状态对比 -->
      <el-dialog
        custom-class="mark-diff-dialog"
        :title="markDiffTitle"
        :visible.sync="markDiffVisible"
        width="820px"
        top="6vh"
        append-to-body
        :close-on-click-modal="false"
      >
        <div class="detail-toolbar mark-diff-toolbar">
          <el-input
            v-model="markDiffKeyword"
            clearable
            size="small"
            placeholder="搜索学生姓名"
            prefix-icon="el-icon-search"
            class="detail-search-input"
            @keyup.enter.native="onMarkDiffSearch"
            @clear="onMarkDiffSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="onMarkDiffSearch"></el-button>
          </el-input>
          <el-checkbox v-model="markDiffChangedOnly" @change="onMarkDiffSearch">仅看变化</el-checkbox>
        </div>
        <el-table
          :data="markDiffPagedList"
          v-loading="markDiffLoading"
          border
          size="small"
          height="420"
          empty-text="暂无对比数据"
        >
          <el-table-column prop="StudentName" label="学生姓名" min-width="100" show-overflow-tooltip />
          <el-table-column prop="ClassName" label="行政班级" min-width="100" show-overflow-tooltip />
          <el-table-column label="上次状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.PrevAttendanceState != null" :type="getStateTag(scope.row.PrevAttendanceState)" size="mini">
                {{ getStateText(scope.row.PrevAttendanceState) }}
              </el-tag>
              <span v-else class="mh-dash">无</span>
            </template>
          </el-table-column>
          <el-table-column label="本次状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStateTag(scope.row.AttendanceState)" size="mini">
                {{ getStateText(scope.row.AttendanceState) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="变化" width="70" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.Changed" type="danger" size="mini" effect="plain">变</el-tag>
              <span v-else class="mh-dash">—</span>
            </template>
          </el-table-column>
          <el-table-column prop="Remark" label="备注" min-width="120" show-overflow-tooltip />
        </el-table>
        <div slot="footer" class="detail-dialog-footer">
          <el-pagination
            class="detail-pager"
            layout="total, sizes, prev, pager, next, jumper"
            :current-page.sync="markDiffPage"
            :page-sizes="[10, 20, 50]"
            :page-size="markDiffPageSize"
            :total="markDiffList.length"
            @size-change="onMarkDiffSizeChange"
            @current-change="onMarkDiffPageChange"
          />
          <el-button size="small" @click="markDiffVisible = false">关闭</el-button>
        </div>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import { getSpecialCourseAttendanceStats, getSpecialCourseAttendanceList, getSpecialCourseMarkBatchHistory, getSpecialCourseMarkBatchStudentDiff, exportSpecialCourseAttendanceStats, exportSpecialCourseAttendanceDetail, getSpecialCourseTimeList } from '../../api/modules/schedule-course';
import { getButtonList } from '../../promissionRouter'
import Toolbar from '../../components/Toolbar'
import DepartmentPane from '../../components/DepartmentPane'

export default {
  name: 'SpecialCourseAttendanceStat',
  components: { Toolbar, DepartmentPane },
  data() {
    const today = new Date().toISOString().substring(0, 10)
    return {
      departmentId: 0,
      filters: {
        statDate: today,
        timeId: '',
        isMarked: ''
      },
      timeSelectOptions: [],
      searchList: [
        { label: '日期', key: 'statDate', type: 'date', width: '160px', initValue: today },
        {
          label: '时段模板',
          key: 'timeId',
          type: 'select',
          clearable: true,
          options: [],
          width: '240px'
        },
        {
          label: '是否点名',
          key: 'isMarked',
          type: 'select',
          clearable: true,
          options: [
            { label: '全部', value: '' },
            { label: '已点名', value: true },
            { label: '未点名', value: false }
          ],
          width: '120px',
          initValue: ''
        }
      ],
      list: [],
      page: 1,
      pageSize: 10,
      listLoading: false,
      buttonList: [],
      exportStatsLoading: false,

      // 明细相关
      detailVisible: false,
      detailTitle: '',
      detailLoading: false,
      detailList: [],
      detailSessionId: null,
      detailDate: null,
      detailPage: 1,
      detailPageSize: 10,
      detailTotal: 0,
      detailKeyword: '',
      /** 明细弹窗考勤状态筛选：''=全部，0缺勤/1正常/2请假/3其他/-1无记录 */
      detailAttendanceState: '',
      exportDetailLoading: false,

      // 点名履历
      markHistoryVisible: false,
      markHistoryTitle: '',
      markHistoryLoading: false,
      markHistoryList: [],
      markHistoryPage: 1,
      markHistoryPageSize: 10,

      // 点名对比（本次 vs 上次）
      markDiffVisible: false,
      markDiffTitle: '',
      markDiffLoading: false,
      markDiffList: [],
      markDiffAllList: [],
      markDiffBatchId: null,
      markDiffKeyword: '',
      markDiffChangedOnly: false,
      markDiffPage: 1,
      markDiffPageSize: 10,
    }
  },
  computed: {
    /** 无菜单按钮时仍显示查询、导出 */
    toolbarButtons() {
      if (this.buttonList && this.buttonList.length > 0) return this.buttonList
      return [
        { name: '查询', Func: 'handleQuery' },
        { name: '导出统计', Func: 'exportStats' }
      ]
    },
    /** 当前页数据（接口仍一次返回全量，前端分页减轻表格渲染压力） */
    pagedList() {
      if (!this.list || !this.list.length) return []
      const start = (this.page - 1) * this.pageSize
      return this.list.slice(start, start + this.pageSize)
    },
    /** 点名履历当前页 */
    markHistoryPagedList() {
      const list = this.markHistoryList || []
      const start = (this.markHistoryPage - 1) * this.markHistoryPageSize
      return list.slice(start, start + this.markHistoryPageSize)
    },
    /** 点名对比当前页 */
    markDiffPagedList() {
      const list = this.markDiffList || []
      const start = (this.markDiffPage - 1) * this.markDiffPageSize
      return list.slice(start, start + this.markDiffPageSize)
    }
  },
  methods: {
    /** 兼容接口 PascalCase / camelCase */
    dutyField(row, camelKey) {
      if (!row) return ''
      const pascal = camelKey.charAt(0).toUpperCase() + camelKey.slice(1)
      const v = row[camelKey] != null ? row[camelKey] : row[pascal]
      return (v != null && String(v).trim() !== '') ? String(v).trim() : ''
    },
    /** 统计行点名时间，兼容 camelCase / PascalCase */
    markedTimeField(row) {
      if (!row) return null
      const v = row.markedTime != null ? row.markedTime : row.MarkedTime
      return v != null && v !== '' ? v : null
    },
    /** 管理端代提交值日、尚无学生点名 → 后端 IsSupplementaryDuty */
    markSupplementary(row) {
      if (!row) return false
      const v = row.isSupplementaryDuty != null ? row.isSupplementaryDuty : row.IsSupplementaryDuty
      return v === true || v === 'true'
    },
    /** 点名时间晚于本节结束 → 后端 IsMakeup（与普通课「已点名【补点】」一致） */
    markIsMakeup(row) {
      if (!row) return false
      const v = row.isMakeup != null ? row.isMakeup : row.IsMakeup
      return v === true || v === 'true'
    },
    /** 实际点名教师，兼容 camelCase / PascalCase */
    markedTeacherField(row) {
      return this.dutyField(row, 'markedTeacher')
    },
    /** 当日点名次数 */
    markCountField(row) {
      if (!row) return 0
      const v = row.markCount != null ? row.markCount : row.MarkCount
      const n = Number(v)
      return Number.isFinite(n) && n > 0 ? n : 0
    },
    /** 打开班次点名履历弹窗 */
    viewMarkHistory(row) {
      if (!row) return
      const sid = row.SessionId || row.sessionId
      const date = row.Date || row.date
      this.markHistoryTitle = `点名履历 - ${row.SessionName || row.sessionName || ''} (${date || ''})`
      this.markHistoryVisible = true
      this.markHistoryList = []
      this.markHistoryPage = 1
      if (!sid) return
      this.markHistoryLoading = true
      getSpecialCourseMarkBatchHistory({ sessionId: sid, date }).then(res => {
        const result = res.data || res
        if (result.success && result.response) {
          this.markHistoryList = result.response || []
        } else {
          this.markHistoryList = []
        }
        this.markHistoryPage = 1
        this.markHistoryLoading = false
      }).catch(() => {
        this.markHistoryLoading = false
        this.markHistoryList = []
        this.markHistoryPage = 1
      })
    },
    onMarkHistorySizeChange(size) {
      this.markHistoryPageSize = size
      this.markHistoryPage = 1
    },
    onMarkHistoryPageChange(p) {
      this.markHistoryPage = p
    },
    /** 打开某次点名相对上次的学生状态对比 */
    viewMarkBatchDiff(row) {
      if (!row) return
      const batchId = row.Id || row.id
      const seq = row.SeqNo != null ? row.SeqNo : row.seqNo
      this.markDiffBatchId = batchId
      this.markDiffTitle = `第${seq}次点名对比` + (seq > 1 ? `（相对第${seq - 1}次）` : '（首次，无上次）')
      this.markDiffVisible = true
      this.markDiffKeyword = ''
      this.markDiffChangedOnly = false
      this.markDiffPage = 1
      this.markDiffList = []
      this.markDiffAllList = []
      this.loadMarkBatchDiff()
    },
    loadMarkBatchDiff() {
      if (!this.markDiffBatchId) return
      this.markDiffLoading = true
      getSpecialCourseMarkBatchStudentDiff({
        batchId: this.markDiffBatchId,
        changedOnly: this.markDiffChangedOnly || undefined,
        keyword: (this.markDiffKeyword || '').trim() || undefined
      }).then(res => {
        const result = res.data || res
        if (result.success && result.response) {
          this.markDiffList = result.response || []
        } else {
          this.markDiffList = []
        }
        this.markDiffPage = 1
        this.markDiffLoading = false
      }).catch(() => {
        this.markDiffLoading = false
        this.markDiffList = []
        this.markDiffPage = 1
      })
    },
    onMarkDiffSearch() {
      this.markDiffPage = 1
      this.loadMarkBatchDiff()
    },
    onMarkDiffSizeChange(size) {
      this.markDiffPageSize = size
      this.markDiffPage = 1
    },
    onMarkDiffPageChange(p) {
      this.markDiffPage = p
    },
    /** 点名状态：卡片顶部强调色 */
    dutyRollBlockClass(row) {
      if (!row) return 'duty-card--pending'
      if (row.IsMarked) return 'duty-card--marked'
      if (this.markSupplementary(row)) return 'duty-card--supp'
      return 'duty-card--pending'
    },
    /** 出勤率数字颜色：0% 用中性灰，避免整列刺眼红字 */
    rateNumStyle(row) {
      const r = this.calculateRate(row)
      if (r === 0) return { color: '#909399' }
      return { color: this.getRateColor(row) }
    },
    /** 进度条颜色：与数字一致 */
    rateProgressColor(row) {
      const r = this.calculateRate(row)
      if (r === 0) return '#dcdfe6'
      return this.getRateColor(row)
    },
    /** 值日(DutyCheck)提交时间，兼容 camelCase / PascalCase */
    dutySubmitTime(row) {
      if (!row) return null
      const v = row.dutyCheckSubmitTime != null ? row.dutyCheckSubmitTime : row.DutyCheckSubmitTime
      return v != null && v !== '' ? v : null
    },
    dutyAttendance(row) {
      if (!row) return 0
      const v = row.dutyAttendance != null ? row.dutyAttendance : row.DutyAttendance
      const n = Number(v)
      return n >= 1 && n <= 3 ? n : 0
    },
    dutyAttendanceText(row) {
      const n = this.dutyAttendance(row)
      return n === 1 ? '正常' : n === 2 ? '迟到' : n === 3 ? '早退' : ''
    },
    dutyAttendanceClass(row) {
      const n = this.dutyAttendance(row)
      if (n === 1) return 'duty-line__v--ok'
      if (n === 2) return 'duty-line__v--warn'
      if (n === 3) return 'duty-line__v--bad'
      return ''
    },
    /**
     * 任教老师展示文案：已点名仅展示 teachingTeachersSummary；未点名/补点再回代代班、值日排班
     */
    dutyTeachingCellDisplay(row) {
      if (!row) return ''
      const teaching = this.dutyField(row, 'teachingTeachersSummary')
      if (teaching) return teaching
      if (row.IsMarked) return ''
      const sub = this.dutyField(row, 'substituteTeachersSummary')
      if (sub) return sub
      return this.dutyField(row, 'scheduledTeachersSummary')
    },
    /**
     * 「原老师」行：已点名不展示；首行教师文案已与排班一致时不重复
     */
    dutyLineScheduledVisible(row) {
      const scheduled = this.dutyField(row, 'scheduledTeachersSummary')
      if (!scheduled || !row || row.IsMarked) return false
      const cell = this.dutyTeachingCellDisplay(row)
      if (!cell) return true
      return cell !== scheduled
    },
    resetTimeFilter() {
      this.filters.timeId = ''
      const toolbar = this.$refs.toolbar
      if (toolbar && toolbar.searchObj) {
        this.$set(toolbar.searchObj, 'timeId', '')
      }
    },
    /** 解析 HH:mm 为当日分钟数 */
    parseHmToMinutes(raw) {
      if (raw == null || raw === '') return null
      const parts = String(raw).trim().split(':')
      if (parts.length < 2) return null
      const h = Number(parts[0])
      const m = Number(parts[1])
      if (Number.isNaN(h) || Number.isNaN(m)) return null
      return h * 60 + m
    },
    /**
     * 按当前时间选择最近时段：落在区间内优先，否则取到起止边界的最短距离
     */
    pickNearestTimeId(options, now) {
      const list = Array.isArray(options) ? options : []
      const clock = now instanceof Date ? now : new Date()
      const nowMin = clock.getHours() * 60 + clock.getMinutes()
      let bestId = null
      let bestDist = Number.POSITIVE_INFINITY
      for (let i = 0; i < list.length; i++) {
        const item = list[i]
        const start = this.parseHmToMinutes(item.startTime)
        const end = this.parseHmToMinutes(item.endTime)
        if (start == null || end == null) continue
        let dist = 0
        if (nowMin < start) dist = start - nowMin
        else if (nowMin > end) dist = nowMin - end
        else dist = 0
        if (dist < bestDist) {
          bestDist = dist
          bestId = item.value
        }
      }
      return bestId
    },
    applyTimeFilterValue(timeId) {
      const val = timeId == null || timeId === '' ? '' : timeId
      this.filters.timeId = val
      const toolbar = this.$refs.toolbar
      if (toolbar && toolbar.searchObj) {
        this.$set(toolbar.searchObj, 'timeId', val)
      }
    },
    formatTimeTemplateLabel(row) {
      const name = (row && (row.TimeName || row.timeName)) || ''
      const campusId = (row && (row.CampusId || row.campusId)) || 0
      const campus = campusId
        ? ((row.CampusName || row.campusName) || '校区')
        : '全校'
      return name ? `${name}（${campus}）` : campus
    },
    patchTimeOptions() {
      const item = this.searchList.find((s) => s.key === 'timeId')
      if (item) {
        // 整表替换，保证 el-select 能看到全部时段（勿裁剪）。最后更新：2026-09-21
        this.$set(item, 'options', (this.timeSelectOptions || []).slice())
      }
    },
    /**
     * 加载本校/本班关联的全部时段模板，并默认选中「当前最近」一项。
     * 注意：只改默认选中值，不按时间过滤 options。最后更新：2026-09-21
     */
    loadTimeOptionsAndApplyDefault() {
      if (!this.departmentId) {
        this.timeSelectOptions = []
        this.patchTimeOptions()
        this.applyTimeFilterValue('')
        return Promise.resolve()
      }
      return getSpecialCourseTimeList({
        departmentId: this.departmentId,
        page: 1,
        pageSize: 500
      }).then((res) => {
        const result = res.data || res
        const page = result.response || result.Response || {}
        const rows = result.success || result.Success
          ? (page.data || page.Data || [])
          : []
        this.timeSelectOptions = (Array.isArray(rows) ? rows : [])
          .filter((r) => r && (r.Id || r.id))
          .map((r) => ({
          label: this.formatTimeTemplateLabel(r),
          value: r.id != null ? r.id : r.Id,
          startTime: r.startTime || r.StartTime || '',
          endTime: r.endTime || r.EndTime || ''
        }))
        this.patchTimeOptions()
        const nearestId = this.pickNearestTimeId(this.timeSelectOptions)
        this.applyTimeFilterValue(nearestId != null ? nearestId : '')
      }).catch(() => {
        this.timeSelectOptions = []
        this.patchTimeOptions()
        this.applyTimeFilterValue('')
      })
    },
    buildQueryParams() {
      const day = this.filters.statDate || null
      const params = {
        departmentId: this.departmentId,
        startDate: day,
        endDate: day
      }
      const tid = this.filters.timeId
      const n = tid != null && tid !== '' ? Number(tid) : 0
      if (n > 0) params.specialCourseTimeId = n
      const marked = this.filters.isMarked
      if (marked === true || marked === false) params.isMarked = marked
      else if (marked === 'true') params.isMarked = true
      else if (marked === 'false') params.isMarked = false
      return params
    },
    async treeNodeClick(data) {
      this.departmentId = data.value
      this.resetTimeFilter()
      await this.loadTimeOptionsAndApplyDefault()
      this.handleQuery()
    },
    handleQuery() {
      if (!this.departmentId) {
        this.$message.error('请先选择机构/学校')
        return
      }
      this.listLoading = true
      getSpecialCourseAttendanceStats(this.buildQueryParams()).then(res => {
        const result = res.data || res
        this.list = result.success ? result.response : []
        this.page = 1
        this.listLoading = false
        this.scheduleTableLayout()
      }).catch(() => { this.listLoading = false })
    },
    /** 窄屏/数据变化后重算表格宽高，保证右侧固定列贴在可视区。最后更新：2026-09-21 */
    scheduleTableLayout() {
      this.$nextTick(() => {
        const table = this.$refs.statTable
        if (table && typeof table.doLayout === 'function') {
          table.doLayout()
        }
      })
    },
    onStatPageChange(p) {
      this.page = p
      this.scheduleTableLayout()
    },
    onStatPageSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.scheduleTableLayout()
    },
    callFunction(item) {
      if (item.search && typeof item.search === 'object') {
        this.filters = Object.assign({}, item.search)
      }
      const fn = item.Func
      if (fn && typeof this[fn] === 'function') {
        this[fn](item)
      } else {
        this.handleQuery()
      }
    },
    /** 触发浏览器下载 blob */
    saveBlobFile(blob, fileName) {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    exportStats() {
      if (!this.departmentId) {
        this.$message.error('请先选择机构/学校')
        return
      }
      this.exportStatsLoading = true
      exportSpecialCourseAttendanceStats(this.buildQueryParams()).then(res => {
        const blob = new Blob([res.data], {
          type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        this.saveBlobFile(blob, `特殊课程考勤统计_${Date.now()}.xlsx`)
        this.$message.success('导出成功')
        this.exportStatsLoading = false
      }).catch(() => {
        this.exportStatsLoading = false
        this.$message.error('导出失败')
      })
    },
    exportDetail() {
      if (!this.detailSessionId || !this.detailDate) {
        this.$message.warning('暂无明细数据')
        return
      }
      this.exportDetailLoading = true
      exportSpecialCourseAttendanceDetail({ sessionId: this.detailSessionId, date: this.detailDate }).then(res => {
        const blob = new Blob([res.data], {
          type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        this.saveBlobFile(blob, `特殊课程考勤明细_${Date.now()}.xlsx`)
        this.$message.success('导出成功')
        this.exportDetailLoading = false
      }).catch(() => {
        this.exportDetailLoading = false
        this.$message.error('导出失败')
      })
    },
    calculateRate(row) {
      if (!row.Total) return 0
      // 出勤率含纯正常 + 迟/退。最后更新：2026-10-29
      const present = (row.Normal || 0) + (row.LateOrEarlyLeave || 0)
      return Math.round((present / row.Total) * 100)
    },
    getRateColor(row) {
      const rate = this.calculateRate(row)
      if (rate >= 95) return '#67c23a'
      if (rate >= 80) return '#e6a23c'
      return '#f56c6c'
    },
    viewDetail(row) {
      this.detailTitle = `考勤明细 - ${row.SessionName} (${row.Date})`
      this.detailSessionId = row.SessionId
      this.detailDate = row.Date
      this.detailKeyword = ''
      this.detailAttendanceState = ''
      this.detailPage = 1
      this.detailVisible = true
      this.loadDetailList()
    },
    /** 明细姓名/状态筛选：回到第 1 页并重新拉取 */
    onDetailSearch() {
      this.detailPage = 1
      this.loadDetailList()
    },
    onDetailSizeChange(size) {
      this.detailPageSize = size
      this.detailPage = 1
      this.loadDetailList()
    },
    onDetailPageChange(p) {
      this.detailPage = p
      this.loadDetailList()
    },
    loadDetailList() {
      if (!this.detailSessionId) return
      this.detailLoading = true
      const params = {
        sessionId: this.detailSessionId,
        date: this.detailDate,
        page: this.detailPage,
        pageSize: this.detailPageSize,
        keyword: (this.detailKeyword || '').trim() || undefined
      }
      if (this.detailAttendanceState !== '' && this.detailAttendanceState != null) {
        params.attendanceState = this.detailAttendanceState
      }
      getSpecialCourseAttendanceList(params).then(res => {
        const result = res.data || res
        if (result.success && result.response) {
          const pm = result.response
          this.detailList = pm.data || []
          this.detailTotal = pm.dataCount != null ? pm.dataCount : 0
        } else {
          this.detailList = []
          this.detailTotal = 0
        }
        this.detailLoading = false
      }).catch(() => {
        this.detailLoading = false
        this.detailList = []
        this.detailTotal = 0
      })
    },
    getStateText(state) {
      const map = { [-1]: '无记录', 0: '缺勤', 1: '正常', 2: '请假', 3: '其他', 4: '迟/退' }
      return map[state] != null ? map[state] : '未知'
    },
    getStateTag(state) {
      const map = { [-1]: 'info', 0: 'danger', 1: 'success', 2: 'warning', 3: 'info', 4: 'warning' }
      return map[state] || ''
    },
    /** 明细状态文案：正常可附带迟到/早退。创建于 2026-10-29 */
    getDetailStateText(row) {
      if (!row) return '未知'
      const state = row.AttendanceState
      if (state !== 1) return this.getStateText(state)
      const isLate = !!(row.IsLate || row.isLate)
      const isEarly = !!(row.IsEarlyLeave || row.isEarlyLeave)
      if (isLate && isEarly) return '正常 · 迟到 · 早退'
      if (isLate) return '正常 · 迟到'
      if (isEarly) return '正常 · 早退'
      return '正常'
    },
    getDetailStateTag(row) {
      if (!row) return ''
      if (row.AttendanceState === 1 && (row.IsLate || row.isLate || row.IsEarlyLeave || row.isEarlyLeave)) {
        return 'warning'
      }
      return this.getStateTag(row.AttendanceState)
    },
    formatTime(time) {
      if (!time) return '-'
      const date = new Date(time)
      const y = date.getFullYear()
      const m = (date.getMonth() + 1).toString().padStart(2, '0')
      const d = date.getDate().toString().padStart(2, '0')
      const h = date.getHours().toString().padStart(2, '0')
      const min = date.getMinutes().toString().padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}`
    }
  },
  mounted() {
    const routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : []
    this.buttonList = getButtonList(this.$route.path, routers)
    this._onStatTableResize = () => this.scheduleTableLayout()
    window.addEventListener('resize', this._onStatTableResize)
  },
  beforeDestroy() {
    if (this._onStatTableResize) {
      window.removeEventListener('resize', this._onStatTableResize)
    }
  }
}
</script>

<style scoped>
/* 与全站 dp-layout 一致：左右独立白卡片 + 中间留白。最后更新：2026-09-21 */
.sc-attendance-layout.dp-layout {
  align-items: stretch;
  box-sizing: border-box;
}

.dp-right.sc-attendance-page {
  padding: 10px 16px 0 !important;
  box-sizing: border-box;
}

.sc-attendance-page > .toolbar {
  padding: 10px 12px !important;
  margin-bottom: 8px;
}

/* 本页强制：筛选项与查询按钮拉开间距。最后更新：2026-09-21 */
.sc-attendance-page > .toolbar /deep/ .toolbar-search-row {
  display: flex !important;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px !important;
}

.sc-attendance-page > .toolbar /deep/ .toolbar-search-item.el-form-item {
  display: inline-flex !important;
  align-items: center;
  margin-right: 0 !important;
}

.sc-attendance-page > .toolbar /deep/ .toolbar-btn-item--query {
  margin-left: 16px !important;
}

.sc-attendance-page > .toolbar /deep/ .el-form--inline {
  margin-bottom: 0;
}

.sc-attendance-page > .toolbar /deep/ .el-button {
  padding: 8px 14px;
}

/* 表格必须跟容器同宽；勿设整表 min-width，否则窄屏会被 tbl-scroll-host 的 overflow:hidden 裁掉右侧固定列。最后更新：2026-09-21 */
.sc-attendance-page .tbl-scroll-host {
  width: 100%;
  min-width: 0;
}

.sc-attendance-page .sc-stat-table {
  width: 100%;
  min-width: 0;
}

/* 与 SpecialCourseTime.vue 等列表页一致：toolbar 行 + 分页右浮动 */
.sc-attendance-page .sc-stat-pager.toolbar {
  padding: 12px 0 14px;
  border-top: 1px solid #ebeef5;
  background: #fff;
  flex: 0 0 auto;
}

.sc-attendance-page .sc-stat-pager.toolbar::after {
  content: '';
  display: table;
  clear: both;
}

.sc-stat-table {
  border: 1px solid #e8ecf1;
  border-radius: 10px;
  overflow: hidden;
}

.sc-stat-table::before {
  display: none;
}

.sc-stat-table /deep/ .el-table__header-wrapper th {
  background: #f7f9fc !important;
  color: #64748b;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;
  border-bottom: 1px solid #e8ecf1 !important;
  padding: 10px 0;
}

.sc-stat-table /deep/ .el-table__row td {
  vertical-align: middle;
  padding: 12px 10px;
  border-bottom-color: #eef2f7;
}

.sc-stat-table /deep/ .el-table__body tr:hover > td {
  background: #f8fbff !important;
}

.sc-stat-table /deep/ .cell {
  line-height: 1.4;
  padding-left: 10px;
  padding-right: 10px;
}

.sc-stat-table /deep/ td.sc-td-session .cell {
  word-break: break-word;
}

.sc-stat-table /deep/ td.sc-td-action .cell,
.sc-stat-table /deep/ .el-table__fixed-right td.sc-td-action .cell {
  text-align: center;
}

.sc-stat-table /deep/ td.sc-td-duty .cell {
  display: flex !important;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}

.sc-action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.sc-stat-table /deep/ .el-table__empty-block {
  min-height: 0;
  padding: 0;
}

.sc-stat-table /deep/ .el-progress-bar__outer {
  border-radius: 999px;
  background-color: #e8ecf1;
}

.sc-stat-table /deep/ .el-progress-bar__inner {
  border-radius: 999px;
}

.sc-stat-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  text-align: center;
  padding: 36px 20px;
  color: #909399;
}

.sc-stat-empty i {
  font-size: 40px;
  color: #dcdfe6;
  margin-bottom: 12px;
}

.sc-stat-empty p {
  margin: 0;
  font-size: 14px;
}

.sc-stat-empty__hint {
  margin-top: 8px !important;
  font-size: 12px;
  color: #c0c4cc;
}

/* 课次列：日期徽章 + 标题 + 班级/范围胶囊 */
.session-cell {
  position: relative;
  padding: 2px 0 2px 12px;
  min-width: 0;
}

.session-cell::before {
  content: '';
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 3px;
  border-radius: 3px;
  background: linear-gradient(180deg, #3b82f6 0%, #93c5fd 100%);
}

.session-cell__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  margin-bottom: 5px;
}

.session-cell__date {
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 700;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 1px 8px;
  border-radius: 999px;
  line-height: 18px;
}

.session-cell__time {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.3;
  min-width: 0;
}

.session-cell__time i {
  font-size: 12px;
  color: #94a3b8;
}

.session-cell__title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  margin-bottom: 6px;
  word-break: break-word;
}

.session-cell__sub {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.session-cell__class {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #334155;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 1px 8px;
  border-radius: 999px;
  line-height: 18px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-cell__scope {
  display: inline-block;
  max-width: 100%;
  font-size: 11px;
  color: #64748b;
  line-height: 1.35;
  cursor: default;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-cell__adj {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.session-cell__adj-text {
  color: #b45309;
  font-size: 11px;
  font-weight: 600;
  cursor: help;
  padding: 0 2px;
}

.session-cell__adj-text:hover {
  color: #92400e;
}

/* 人数：3 列网格（含迟/退共 6 项） */
.stat-chips {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 5px;
  width: 100%;
  min-width: 196px;
  margin: 0 auto;
}

.stat-chip--late {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #c2410c;
}

.stat-chip--late em {
  color: #ea580c;
}

.mh-chip--late {
  background: #fff7ed;
  color: #c2410c;
}

.stat-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: 11px;
  padding: 4px 6px;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  line-height: 1.2;
  white-space: nowrap;
  min-width: 0;
  border: 1px solid #e8ecf1;
}

.stat-chip em {
  font-style: normal;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
}

.stat-chip strong {
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.stat-chip--total {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.stat-chip--total em { color: #60a5fa; }

.stat-chip--ok {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #15803d;
}

.stat-chip--ok em { color: #86efac; }

.stat-chip--bad {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}

.stat-chip--bad em { color: #fca5a5; }

.stat-chip--warn {
  background: #fffbeb;
  border-color: #fde68a;
  color: #b45309;
}

.stat-chip--warn em { color: #fcd34d; }

.stat-chip--muted {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #64748b;
}

.stat-chip--muted em { color: #cbd5e1; }

.rate-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  width: 100%;
}

.rate-cell__num {
  display: block;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 5px;
  letter-spacing: -0.02em;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.rate-cell__bar {
  width: 100%;
  max-width: 76px;
}

.duty-card {
  display: block;
  width: 100% !important;
  min-width: 100%;
  max-width: 100%;
  align-self: stretch;
  box-sizing: border-box;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e8ecf1;
  overflow: hidden;
}

.duty-card--marked {
  border-color: #bbf7d0;
  background: linear-gradient(180deg, #f7fdf9 0%, #fff 40%);
}

.duty-card--supp {
  border-color: #fde68a;
  background: linear-gradient(180deg, #fffbeb 0%, #fff 40%);
}

.duty-card--pending {
  border-color: #e2e8f0;
  background: #fafbfc;
}

.duty-card__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  padding: 7px 10px;
  width: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid #eef2f7;
}

.duty-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  padding: 3px 9px;
  border-radius: 999px;
}

.duty-badge--ok {
  color: #166534;
  background: #dcfce7;
}

.duty-badge--warn {
  color: #92400e;
  background: #fef3c7;
}

.duty-badge--muted {
  color: #64748b;
  background: #e2e8f0;
}

.duty-mark-count-btn {
  padding: 0 !important;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb !important;
}

.mark-history-empty {
  margin: 12px 0 0;
  color: #909399;
  font-size: 13px;
  line-height: 1.5;
}

.mark-diff-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.duty-card__time {
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.duty-card__body {
  padding: 7px 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.duty-teachers {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px 14px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.duty-teachers__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 1 auto;
  min-width: 0;
  white-space: nowrap;
}

.duty-teachers__item .duty-line__v {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 7em;
}

.duty-line {
  display: block;
  font-size: 12px;
  line-height: 1.45;
  color: #334155;
}

.duty-line--inline {
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 两段信息 + 间隔号：整段行内流式 */
.duty-line--pair {
  line-height: 1.55;
}

.duty-group {
  display: inline;
  white-space: nowrap;
}

.duty-pair__dot {
  display: inline;
  margin: 0 6px;
  color: #dcdfe6;
  font-size: 13px;
  line-height: 1;
  user-select: none;
  vertical-align: 1px;
}

.duty-line__k {
  display: inline;
  margin-right: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #909399;
  letter-spacing: 0.02em;
  vertical-align: baseline;
}

.duty-line__v {
  display: inline;
  color: #303133;
  font-weight: 500;
  vertical-align: baseline;
  word-break: break-all;
}

.duty-line__v--empty {
  color: #c0c4cc;
  font-weight: 400;
}

.duty-line__v--accent {
  color: #b88230;
}

.duty-line__v--ok {
  color: #67c23a;
  font-weight: 600;
}

.duty-line__v--warn {
  color: #e6a23c;
  font-weight: 600;
}

.duty-line__v--bad {
  color: #f56c6c;
  font-weight: 600;
}

.duty-line__v--mono {
  font-variant-numeric: tabular-nums;
  color: #606266;
  font-weight: 400;
}


.result-success-tip {
  text-align: center;
  padding: 30px 0;
  color: #67c23a;
}

.detail-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px 16px;
  width: 100%;
}

.detail-dialog-footer /deep/ .detail-pager.el-pagination {
  padding: 0;
  margin: 0;
}

.detail-dialog /deep/ .el-dialog__header {
  display: flex;
  align-items: center;
  padding-right: 16px;
}
.detail-dialog /deep/ .el-dialog__title {
  flex: 1;
  min-width: 0;
  float: none;
  line-height: 1.4;
}
.detail-dialog-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding-right: 40px;
}
.detail-dialog-title-text {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.detail-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.detail-search-input {
  width: 260px;
}

.detail-state-select {
  width: 140px;
}

.mh-body {
  min-height: 420px;
}

.mh-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.mh-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  border: 1px solid transparent;
}

.mh-chip--total {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #bfdbfe;
}

.mh-chip--ok {
  color: #15803d;
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.mh-chip--warn {
  color: #b45309;
  background: #fffbeb;
  border-color: #fde68a;
}

.mh-chip--bad {
  color: #b91c1c;
  background: #fef2f2;
  border-color: #fecaca;
}

.mh-chip--muted {
  color: #64748b;
  background: #f8fafc;
  border-color: #e2e8f0;
}

.mh-seq {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.mh-dash {
  color: #c0c4cc;
}
</style>

<style>
/* append-to-body：点名履历 / 对比弹窗外壳加高 */
.mark-history-dialog,
.mark-diff-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(47, 62, 82, 0.16);
}

.mark-history-dialog .el-dialog__header,
.mark-diff-dialog .el-dialog__header {
  padding: 16px 20px 12px;
  background: linear-gradient(180deg, #fafbfd 0%, #fff 100%);
  border-bottom: 1px solid #ebeef5;
}

.mark-history-dialog .el-dialog__title,
.mark-diff-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2a37;
  line-height: 1.4;
}

.mark-history-dialog .el-dialog__body,
.mark-diff-dialog .el-dialog__body {
  padding: 16px 20px 12px;
  min-height: 460px;
  background: #f8fafc;
}

.mark-history-dialog .el-dialog__footer,
.mark-diff-dialog .el-dialog__footer {
  padding: 12px 20px 16px;
  background: #fff;
  border-top: 1px solid #ebeef5;
}

.mark-history-dialog .mh-table,
.mark-diff-dialog .el-table {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}
</style>

